import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/base";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OPEN_EVENT, readConsent, writeConsent } from "@/lib/consent";
import { loadPixel } from "@/lib/meta";

const BTN = "h-10 flex-1 whitespace-nowrap rounded-full border border-line bg-surface px-2 text-[13px] font-medium text-ink hover:bg-surface-2 sm:flex-none sm:min-w-[150px] sm:px-4 sm:text-[14px]";
const CATS = [
  ["necessary", "Nécessaires", "Session, sécurité, thème. Toujours actifs."],
  ["audience", "Mesure d'audience", "Notre propre mesure du parcours, sans outil tiers."],
  ["ads", "Publicité", "Pixel Meta et API Conversions, pour mesurer nos publicités."],
  ["thirdparty", "Contenus tiers", "Le calendrier Calendly sur la page Session."],
];

const save = (c) => {
  const v = writeConsent(c);
  // audit P3: the server-stored consent gates ads, so the pixel config is asked only once the choice is saved
  api.post("/consent", { audience: v.audience, ads: v.ads, thirdparty: v.thirdparty }).catch(() => {}).finally(() => { if (v.ads) loadPixel(); });
};

export default function CookieBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(() => !readConsent());
  const [choose, setChoose] = useState(false);
  const [c, setC] = useState(() => readConsent() || { audience: true, ads: false, thirdparty: false });

  useEffect(() => {
    const reopen = () => { setC(readConsent() || { audience: true, ads: false, thirdparty: false }); setChoose(true); setOpen(true); };
    window.addEventListener(OPEN_EVENT, reopen);
    loadPixel();
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  useEffect(() => {
    const cur = readConsent();
    if (user?.id && cur) api.post("/consent", { audience: cur.audience, ads: cur.ads, thirdparty: cur.thirdparty }).catch(() => {});
  }, [user?.id]);

  if (!open) return null;
  const done = (v) => { save(v); setOpen(false); setChoose(false); };
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-6" data-testid="cookie-banner" role="dialog" aria-label="Cookies">
      <div className="mx-auto max-w-[720px] rounded-card border border-line bg-surface p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] sm:p-5">
        <p className="text-[14px] leading-relaxed text-ink">
          On utilise des cookies pour faire marcher archijo, mesurer l'audience nous-mêmes et, si tu l'acceptes, pour la publicité et le calendrier Calendly.{" "}
          <Link to="/confidentialite" className="underline" data-testid="cookie-privacy-link">En savoir plus</Link>
        </p>
        {choose && (
          <div className="mt-3 flex flex-col divide-y divide-line border-y border-line" data-testid="cookie-choices">
            {CATS.map(([k, label, hint]) => (
              <div key={k} className="flex items-center justify-between gap-4 py-3">
                <span className="flex flex-col"><span className="text-[14px] font-medium text-ink">{label}</span><span className="text-[13px] text-muted">{hint}</span></span>
                {k === "necessary"
                  ? <span className="text-[13px] text-muted" data-testid="cookie-necessary">Toujours</span>
                  : <Switch checked={!!c[k]} onChange={(v) => setC((s) => ({ ...s, [k]: v }))} testId={`cookie-switch-${k}`} label={label} />}
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={BTN} onClick={() => done({ audience: true, ads: true, thirdparty: true })} data-testid="cookie-accept-all">Tout accepter</button>
          <button type="button" className={BTN} onClick={() => done({ audience: false, ads: false, thirdparty: false })} data-testid="cookie-reject-all">Tout refuser</button>
          {choose
            ? <button type="button" className={BTN} onClick={() => done(c)} data-testid="cookie-save">Enregistrer</button>
            : <button type="button" className={BTN} onClick={() => setChoose(true)} data-testid="cookie-choose">Choisir</button>}
        </div>
      </div>
    </div>
  );
}
